import formName from 'constants/formName'
import { actions, actionTypes, selectors } from 'data'
import { notifySuccess, notifyWarning } from 'data/App/sagas'
import {
  AccumulatorResponseType,
  AuthMessageType,
  FeesResponseType,
  LimitsType,
  TierDefinitionType,
  TiersLimitsVolumeResponseType,
  TierType,
  UserType,
  WithdrawalLimitResponseType,
  WithdrawalPeriodType,
  WithdrawalPurposeType
} from 'data/Auth/types'
import isNil from 'lodash/isNil'
import { call, delay, fork, put, select, takeEvery } from 'redux-saga/effects'
import { refreshToken, signout } from 'services/api/auth'
import { current, getFees, getLimits } from 'services/api/user'
import webSocket from 'services/api/webSocket'
import { addTiming, removeUser } from 'utils/datadog'
import { Response } from 'utils/fetchApi'
import {
  getCSRFToken,
  getExpirationTime,
  getJWTToken,
  removeExpirationTime,
  removeJWTToken
} from 'utils/tokenHelper'
import { isEmailVerified } from 'utils/user'

import settingsSagas from '../Settings/sagas'
import extraKYCSagas from './ExtraKYCQuestions/sagas'
import localSettingsSagas from './localSettings/sagas'
import secondFactorChallengeMainSagas from './SecondFactorChallenge/sagas'
import UKFrictionsSagas from './UKFrictions/sagas'

const POLL_USER_DELAY = 5000

const checkSession = function* () {
  const e = getExpirationTime()

  if (isNil(e) || new Date(parseInt(e, 10)) <= new Date()) {
    localStorage.removeItem('e')

    yield put(actions.auth.setUser({ error: { cause: 'Unauth' } }))
    yield put(actions.auth.authed(false))
    return
  }

  const data: Response<UserType> = yield call(current)

  if (!data.ok) {
    localStorage.removeItem('e')

    yield put(actions.auth.setUser({ error: data.error }))
    yield put(actions.auth.authed(false))
    return
  }

  yield put(
    actions.auth.setUser({
      user: data.response
    })
  )

  yield put(actions.auth.authed(true))
  yield put(actions.app.hideModal('SIGNUP'))
}

const getUser = function* () {
  const data: Response<UserType> = yield call(current)

  if (data.ok) {
    yield put(
      actions.auth.setUser({
        user: data.response
      })
    )
  }
}

const parseTiers = (withdrawalLimits?: Array<WithdrawalLimitResponseType>) => {
  const tiers: TierDefinitionType = {}
  if (withdrawalLimits) {
    withdrawalLimits.forEach((t) => {
      const entry = tiers[t.tier] || {}
      const purpose = entry[t.purpose] || {}
      purpose[t.type] = t.quantity
      entry[t.purpose] = purpose
      tiers[t.tier] = entry
    })
  }
  return tiers
}

const parseLimits = (
  withdrawalAccumulators: Array<AccumulatorResponseType>,
  tiers: TierDefinitionType,
  currentTier: TierType
) => {
  const limits: LimitsType = {}
  if (!isNil(currentTier) && withdrawalAccumulators) {
    withdrawalAccumulators.forEach((w) => {
      const withdrawalType: WithdrawalPurposeType = w.isFiat
        ? 'WITHDRAWAL_FIAT'
        : 'WITHDRAWAL_CRYPTO'
      const periodVal: WithdrawalPeriodType = w.type
      const tier = tiers[currentTier] || {}
      const withdrawal = tier[withdrawalType] || {}
      const limitVal = withdrawal[periodVal] || 0

      const periodLimit = limits[withdrawalType] || {}
      periodLimit[periodVal] = {
        limit: limitVal,
        used: Number(w.amount.value)
      }
      limits[withdrawalType] = periodLimit
    })
  }
  return limits
}

const parseVolume = (ordersEventsAccumulators?: Array<AccumulatorResponseType>) => {
  let volume = 0
  if (ordersEventsAccumulators) {
    volume = ordersEventsAccumulators.reduce(
      (accumulator, currentValue) =>
        currentValue.type === 'MONTH'
          ? accumulator + Number(currentValue.amount.value)
          : accumulator,
      volume
    )
  }
  return volume
}

const getLimitsSaga = function* () {
  const data: Response<TiersLimitsVolumeResponseType> = yield call(getLimits)
  if (!data.ok) return
  const { response } = data

  const tiers: TierDefinitionType = parseTiers(response.withdrawalLimits)
  const currentTier: TierType = yield select(selectors.auth.getUserTiersCurrent)
  const limits: LimitsType = parseLimits(response.withdrawalAccumulators, tiers, currentTier)
  const volume = parseVolume(response.ordersEventsAccumulators)

  yield put(
    actions.auth.setUserLimits({
      limits,
      tiers,
      volume
    })
  )
}

const getUserFees = function* () {
  const authenticated = yield select(selectors.auth.isAuthenticated)
  if (!authenticated) return

  const data: Response<FeesResponseType> = yield call(getFees)
  if (!data.ok) return
  const { fees, feesAccumulators } = data.response

  const promos = {}

  for (let i = 0; i < Object.keys(fees).length; i += 1) {
    const key = Object.keys(fees)[i]
    if (fees[key]?.active?.overrideFromDefault || key === 'all') {
      promos[key] = fees[key]
    }
  }

  yield put(actions.auth.setUserAggregateFees(feesAccumulators))
  yield put(actions.auth.setUserPromos(promos))
  yield put(actions.auth.setUserFees(fees))
}

const signoutSaga = function* () {
  yield put(actions.form.reset(formName.TICKET))
  yield put(actions.balance.reset())
  yield put(actions.historical.setOrders([]))
  const config = yield select(selectors.config.getConfig)

  const data: Response<void> = yield call(signout)

  removeJWTToken()
  removeExpirationTime()

  if (data.ok) {
    yield call(notifySuccess, 'OK_SIGNOUT')
    yield put(actions.auth.checkSession())

    if (config.datadog) {
      removeUser()
    }
  }
}

const messageManager = function* (action) {
  const message: AuthMessageType = action.payload

  if (message.event === webSocket.event.REJECTED) {
    yield put(actions.auth.setSocketAuthed(false))
    return
  }

  if (message.event === webSocket.event.SUBSCRIBED) {
    performance.mark('auth-subscribed')
    performance.measure('socket-auth', 'auth-subscribe', 'auth-subscribed')
    addTiming('balances-loaded')
    yield put(actions.auth.setSocketAuthed(true))
  }
}

const start = function* () {
  const authenticated = yield select(selectors.auth.isAuthenticated)
  const csrfToken = getCSRFToken()
  const ws = yield select(selectors.socket.getConnection)

  if (!authenticated || !csrfToken || !ws) {
    return
  }

  performance.mark('auth-subscribe')
  const channel = webSocket.channel.AUTH
  yield put(
    actions.socket.subscribe({
      channel,
      csrf_token: csrfToken,
      token: getJWTToken() ?? undefined
    })
  )
}

function* processKYCUpdate(prevUser, newUser) {
  if (newUser.kycState === 'UNDER_REVIEW') {
    yield call(notifyWarning, 'KYC_UNDER_REVIEW', 20)
    return
  }

  if (newUser.kycState === 'VERIFIED') {
    yield call(notifySuccess, 'KYC_VERIFIED', 20)
  }
}

const pollUser = function* () {
  while (true) {
    const data: Response<UserType> = yield call(current)
    if (!data.ok) {
      yield delay(POLL_USER_DELAY)
      return
    }

    const newUser: UserType = data.response
    const prevUser: UserType = yield select(selectors.auth.getUser)
    yield put(
      actions.auth.setUser({
        user: newUser
      })
    )

    if (isEmailVerified(newUser) && (!prevUser || isEmailVerified(prevUser))) {
      yield call(processKYCUpdate, prevUser, newUser)
      return
    }

    if (
      prevUser &&
      newUser &&
      prevUser.kycState !== 'VERIFIED' &&
      newUser.kycState !== prevUser.kycState
    ) {
      yield call(processKYCUpdate, prevUser, newUser)
      return
    }
    yield delay(POLL_USER_DELAY)
  }
}

const scheduleTokenRefresh = function* () {
  let user = yield select(selectors.auth.getUser)
  const expireTime = getExpirationTime()
  if (!user || !expireTime) {
    // logged out so no need to check
    return
  }

  // Miliseconds until 10 seconds to expire
  const time = new Date(parseInt(expireTime, 10)).getTime() - Date.now() - 10 * 1000
  // sleep until we are close to expire
  yield delay(time)
  user = yield select(selectors.auth.getUser)
  if (!user) {
    // logged out so no need to check
    return
  }

  yield call(refreshToken, true)
  yield call(scheduleTokenRefresh)
}

export default function* authMain() {
  yield takeEvery(actionTypes.auth.CHECK_SESSION, checkSession)
  yield takeEvery(actionTypes.auth.POLL_USER, pollUser)
  yield takeEvery(actionTypes.auth.GET_USER, getUser)
  yield takeEvery(actionTypes.auth.GET_USER_LIMITS, getLimitsSaga)
  yield takeEvery(actionTypes.auth.GET_USER_FEES, getUserFees)
  yield takeEvery(actionTypes.auth.SIGNOUT, signoutSaga)

  yield takeEvery(actionTypes.auth.AUTHED, getUserFees)
  yield takeEvery(actionTypes.auth.AUTHED, scheduleTokenRefresh)

  // start listens to both AUTHED and SET_CONNECTION because both may need a resubscription
  // Use cases: connection to WS comes first but token has not been validated yet
  // Use cases: we are resubscribing to WS after a successful session that got interrupted
  yield takeEvery(actionTypes.auth.AUTHED, start)
  yield takeEvery(actionTypes.socket.SET_CONNECTION, start)
  const channel = webSocket.channel.AUTH
  yield takeEvery(
    (action) => action.type === actionTypes.socket.RECEIVED && action.payload.channel === channel,
    messageManager
  )

  yield fork(localSettingsSagas)
  yield fork(settingsSagas)
  yield fork(secondFactorChallengeMainSagas)
  yield fork(extraKYCSagas)
  yield fork(UKFrictionsSagas)
}
